import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Label } from '@clarix/ui';
import { useSignup, getApiErrorMessage } from '@/core/api';

/**
 * Step 1 of self-serve workspace signup: just an email. Owner name, password
 * and the workspace's own details are collected together in the next step
 * (SignupWorkspacePage), once the email is OTP-verified — there is no
 * separate "create an account" step anymore, since the owner account is
 * created atomically, inside the new tenant database, by workspace creation
 * itself.
 */
export function SignupPage() {
  const navigate = useNavigate();
  const signup = useSignup();
  const [email, setEmail] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setFieldError('Enter a valid email address.');
      return;
    }
    setFieldError(null);
    try {
      await signup.mutateAsync({ email: trimmed });
      navigate(`/verify-otp?email=${encodeURIComponent(trimmed)}`);
    } catch {
      /* error surfaced below */
    }
  };

  const serverError = signup.error ? getApiErrorMessage(signup.error) : null;

  return (
    <div>
      <h1 className="mb-1 text-5xl font-semibold text-slate-900 dark:text-slate-100">
        Create your Clarix workspace
      </h1>
      <p className="mb-6 text-xl text-slate-500">
        Already have one?{' '}
        <Link to="/login" className="font-medium text-indigo-600 hover:underline">
          Sign in
        </Link>
      </p>

      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div>
          <Label htmlFor="email">Work email</Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            placeholder="you@company.com"
            value={email}
            aria-invalid={!!fieldError || undefined}
            onChange={(e) => {
              setEmail(e.target.value);
              setFieldError(null);
            }}
          />
          {fieldError && (
            <p className="mt-1 text-lg text-red-600 dark:text-red-400">{fieldError}</p>
          )}
          <p className="mt-1 text-lg text-slate-500">
            We&apos;ll email you a 6-digit code to verify it&apos;s you.
          </p>
        </div>

        {serverError && (
          <div className="rounded-md bg-red-50 px-3 py-2 text-xl text-red-700 dark:bg-red-950/40 dark:text-red-300">
            {serverError}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={signup.isPending}>
          {signup.isPending ? 'Sending code…' : 'Continue'}
        </Button>
      </form>
    </div>
  );
}
